import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { Banner } from '../../ui/Banner.js';
import { Button } from '../../ui/Button.js';
import { useSession, type SignInFailure } from '../../lib/session.js';
import { chooseSignInView } from './sign-in-view.js';

/**
 * The way in, and the only screen a signed-out admin can reach.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE BUTTON WAITS TO BE PRESSED
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The product console redirects to the provider as soon as it finds no token,
 * because everybody who opens it is meant to be signed in. This one asks first.
 * Somebody who lands here by mistake should see what this console is before
 * their browser is sent anywhere, and an automatic redirect turns a page that
 * fails to render into an endless bounce through the provider.
 *
 * ── THE TWO STATES THAT ARE NOT "READY" ───────────────────────────────────
 *
 * NOT CONFIGURED: the platform mounts its admin surface only when an admin
 * audience is set, so `/api/admin/auth/config` answers 404 on a laptop in dev
 * mode. The screen says so rather than offering a button that cannot work — a
 * disabled control with a reason is honest; one that fails on click teaches
 * somebody the console is broken.
 *
 * FAILED: a sign-in that did not finish, named by its cause. "Sign-in failed"
 * for all of them tells a person nothing about what to do next, and two of the
 * causes here — a state mismatch, a nonce mismatch — are worth telling somebody
 * about rather than retrying.
 */
export function SignIn() {
  const { signInConfig, devAdmins, signInError, signIn, signInAs } = useSession();
  const [leaving, setLeaving] = useState(false);

  /*
    One decision, made once. See sign-in-view.ts for why this is a function and
    not a chain of conditions in the markup below.
  */
  const view = chooseSignInView(signInError, signInConfig);

  return (
    <main className="cosmos-v2 flex min-h-screen items-center justify-center bg-canvas px-6">
      <div className="flex w-full max-w-md flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-primary">{t('app.name')}</h1>
          <p className="text-sm text-tertiary">{t('app.tagline')}</p>
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-medium text-primary">{t('signIn.title')}</h2>
          <p className="text-md text-secondary">{t('signIn.body')}</p>
        </div>

        {view.reason ? <SignInFailed reason={view.reason} /> : null}

        {/*
          EXACTLY ONE OF THESE RENDERS, and that is now the type's doing rather
          than this file's discipline.

          It was this file's discipline twice, and twice it failed the same way:
          an unreachable platform set `signInError` AND left `signInConfig`
          null, so the screen showed two banners giving opposite diagnoses of
          one situation. The second attempt chained the config states and left
          the error banner rendering independently above them, which fixed
          nothing and left a comment saying it had.

          `chooseSignInView` returns one value. There is no longer a place to
          put a second banner.
        */}
        {view.kind === 'unreachable' ? null : view.kind === 'not-configured' ? (
          <Banner intent="info" title={t('signIn.notConfiguredTitle')}>
            {t('signIn.notConfiguredBody')}
          </Banner>
        ) : view.kind === 'dev' ? (
          <div className="flex flex-col gap-3">
            <Banner intent="info" title={t('signIn.devTitle')}>
              {t('signIn.devBody')}
            </Banner>
            {devAdmins.length === 0 ? (
              <p className="text-sm text-tertiary">{t('signIn.devEmpty')}</p>
            ) : (
              devAdmins.map((who) => (
                <Button
                  key={who.email}
                  variant="secondary"
                  size="lg"
                  onClick={() => void signInAs(who.email)}
                >
                  {who.email}
                </Button>
              ))
            )}
          </div>
        ) : (
          <Button
            variant="primary"
            size="lg"
            disabled={leaving}
            onClick={() => {
              // `beginSignIn` navigates away and does not return, so this stays
              // true for the life of the page. It exists to stop a second click
              // starting a second flow and overwriting the first's verifier.
              setLeaving(true);
              void signIn();
            }}
          >
            {leaving ? t('signIn.working') : t('signIn.action')}
          </Button>
        )}
      </div>
    </main>
  );
}

/**
 * Ten reasons, five messages, chosen HERE rather than in the session module.
 *
 * Words belong in the view, and a lookup table keyed by reason would hide every
 * key from the unused-message detector — it only sees a literal inside `t()`.
 *
 * The grouping is by what the reader should DO, which is why `wrongIssuer` is
 * split out rather than folded in with the other unreachable cases the way the
 * product console folds it. There the advice is the same either way. Here it is
 * not: an unreachable provider means try again in a moment, and a discovery
 * document naming a different issuer means stop and tell somebody. This console
 * can destroy customer data, so the two must not read alike.
 */
function SignInFailed({ reason }: { reason: SignInFailure }) {
  const { title, body } = (() => {
    switch (reason) {
      case 'cancelled':
        // Not a fault at all. Calling it a failure sends somebody looking for a
        // problem they do not have.
        return { title: t('signInError.cancelled'), body: t('signInError.cancelledBody') };
      case 'not-an-admin':
        // The one case where trying again cannot help: the sign-in already
        // worked. What failed is the roster check, and that is a decision, not
        // an error.
        return { title: t('signInError.notAnAdmin'), body: t('signInError.notAnAdminBody') };
      case 'not-this-browser':
      case 'nonce-mismatch':
        // Both mean the response did not belong to the request this tab made.
        // Worth saying plainly: one of the ways that happens is somebody else
        // feeding you their authorisation code.
        return { title: t('signInError.unverified'), body: t('signInError.unverifiedBody') };
      case 'issuer-mismatch':
        return { title: t('signInError.wrongIssuer'), body: t('signInError.wrongIssuerBody') };
      case 'unreachable':
      case 'endpoints-missing':
      case 'platform-unreachable':
        return { title: t('signInError.unreachable'), body: t('signInError.unreachableBody') };
      default:
        return { title: t('signInError.failed'), body: t('signInError.failedBody') };
    }
  })();

  return (
    <Banner intent="danger" title={title}>
      {body}
    </Banner>
  );
}
